// Pure allocation maths for the Allocation Calculator — no React, no fetches.
// Weights are the portal's effective TARGET weights (approved trading targets,
// then platform targets), delivered by the holdings endpoint per portfolio code.

// Group headers ARE the investment-config broad asset classes (Aaron
// 2026-09-10). This replaces the earlier PDS-style sector fold, under which
// Private Debt, Private Equity, Commodities and Global Alpha all collapsed into
// "Alternatives" while the direct-pick dropdown offered the taxonomy names —
// so a "Private Debt — direct allocation" line rendered under an "Alternatives"
// heading. Both the holdings endpoint and the funds endpoint now resolve every
// line to a broad class server-side (Sub Asset Class → taxonomy), so no
// client-side relabelling remains; a class the taxonomy doesn't know surfaces
// under its own raw name rather than being hidden inside another group.

// Client-facing curation of the direct-allocation dropdown (Aaron 2026-08-25):
// the core broad classes plus the private-markets classes. Internal taxonomy
// labels (FX, Global Alpha - G/D, Real Assets (Private Market)) are
// deliberately NOT offered as direct picks — an unmapped label would surface
// as its own raw-named sector in the output. The app intersects this with the
// live registry taxonomy, so a class renamed or removed there disappears here
// rather than going stale.
export const DIRECT_ASSET_CLASSES = [
  'Australian Equities',
  'International Equities',
  'Real Estate',
  'Infrastructure',
  'Global Alpha',
  'Government Bonds',
  'Corporate Debt',
  'Cash',
  'Private Equity',
  'Private Debt',
  'Private Infrastructure',
  'Private Real Estate',
  'Commodities',
];

// Footnote attached wherever a manually added component appears (screen, Excel,
// PDF). Brand-agnostic on purpose — the PDF may carry white-label branding, so
// the classification is attributed to the adviser rather than naming Drummond.
export const CUSTOM_FUND_NOTE =
  '* Manually added investment option — asset class classification supplied by the adviser.';

// Footnote shown whenever the comparison view is on: the current side is pure
// adviser input (percentages and retained $), never portal data.
export const CURRENT_WEIGHTS_NOTE =
  'Current weights and amounts were supplied by the adviser and have not been independently verified.';

// "Type" choices offered for a manual component; the modal also accepts free
// text ("Other…"). The label is builder-only — it never reaches the outputs.
export const MANUAL_TYPE_OPTIONS = ['Portfolio', 'Fund', 'Asset class'];

export const sectorFor = (assetClassGroup) => (assetClassGroup || '').trim() || 'Other';

// Group header order: investment-config display order (`classOrder` = the
// registry's broad_asset_classes names, already sorted by display_order), then
// any class outside that list alphabetically, with Cash always last.
export const sectorSortKey = (sector, classOrder = []) => {
  if (sector === 'Cash') return [2, 0, sector];
  const idx = classOrder.indexOf(sector);
  if (idx >= 0) return [0, idx, sector];
  return [1, 0, sector];
};

// Largest-remainder rounding (port of round_weights_1dp in app/apps/portfolio/api.py,
// generalised to integer units): round `values` (exact floats in units) to integers
// summing to exactly `targetUnits`. Residual units go to the largest fractional
// remainders; excess comes back from the smallest.
export function largestRemainderRound(values, targetUnits) {
  const entries = values.map((v, i) => {
    const x = Number.isFinite(v) ? v : 0;
    const floor = Math.floor(x + 1e-9);
    return { i, floor, rem: x - floor };
  });
  let residual = Math.round(targetUnits) - entries.reduce((s, e) => s + e.floor, 0);
  if (residual > 0) {
    [...entries].sort((a, b) => b.rem - a.rem).slice(0, residual).forEach((e) => { e.floor += 1; });
  } else if (residual < 0) {
    [...entries].sort((a, b) => a.rem - b.rem).slice(0, -residual).forEach((e) => { e.floor -= 1; });
  }
  const out = new Array(values.length);
  entries.forEach((e) => { out[e.i] = e.floor; });
  return out;
}

const round2 = (n) => Math.round(n * 100) / 100;
// Sum percentages in integer hundredths so float drift can't show "100.00000000000001".
const sumPct = (rows, field) => rows.reduce((s, r) => s + Math.round((r[field] || 0) * 100), 0) / 100;
const sumOf = (rows, field) => rows.reduce((s, r) => s + (r[field] || 0), 0);

const empty = (warnings, errors) => ({
  retainedRows: [], sectors: [], totalRow: null, exposure: null,
  comparison: false, warnings, errors,
});

/**
 * Build the allocation table rows from the blend components.
 *
 * @param total         total investment in whole dollars (> 0)
 * @param retainedItems [{ name, amount, current }] dollars held off the top — presented
 *                      as separate lines (e.g. Retained Cash, Term Deposits) but all
 *                      behaving identically: off the top, defensive (Aaron 2026-08-25).
 *                      `amount` is the target $; `current` (comparison only) today's $.
 * @param components    [{ type: 'portfolio'|'lab'|'fund'|'manual'|'direct', ref, share, current }]
 *                      share   = target weight %; current = current weight % (comparison only)
 *                      portfolio: ref = portfolio_code (string)
 *                      lab:       ref = saved Portfolio Lab portfolio id (string) — Drummond only
 *                      fund:      ref = { apir_code, tr_code, fund_name, asset_class_group }
 *                      manual:    ref = { fund_name, apir_code, tr_code:'', asset_class_group,
 *                                         is_custom: true, custom_type } — adviser-typed,
 *                                         classification supplied by the adviser
 *                      direct:    ref = broad asset class name (string)
 * @param holdingsByCode { code: [{ asset_name, apir_code, tr_code, asset_class_group, weight }] }
 * @param nameByCode     { code: display_name } for error messages
 * @param labById        { id: { name, geared, unmatched: [{fund_code, fund_name, reason}],
 *                         holdings: [same shape as holdingsByCode rows] } } — a geared
 *                       portfolio, or one with any holding not matched to the AP List, is
 *                       an error, never partially allocated (Aaron 2026-10-13)
 * @param growthClasses  broad asset class names counted as growth (from the registry
 *                       taxonomy); everything else — retained items included — is defensive
 * @param classOrder     broad asset class names in investment-config display order;
 *                       fixes the order of the group headers (Cash always last)
 * @param comparison     when true the current side is built alongside the target side
 *                       (Aaron 2026-09-10): both must foot to 100% independently, every
 *                       row carries curPct/curDollars/changePct/changeDollars, and rows
 *                       present on only one side stay in the table with 0.00% on the other.
 * @returns { retainedRows, sectors, totalRow, exposure, comparison, warnings, errors }
 */
export function buildAllocation({
  total, retainedItems = [], components, holdingsByCode, nameByCode = {}, labById = {},
  growthClasses = [], classOrder = [], comparison = false,
}) {
  const warnings = [];
  const errors = [];
  // Each side is a complete allocation in its own right. Without comparison
  // there is only the target side and the messages keep their original wording.
  const sides = comparison ? ['current', 'target'] : ['target'];
  const weightOf = (c, side) => (side === 'target' ? c.share : c.current) || 0;
  const retainedOf = (r, side) => (side === 'target' ? r.amount : r.current) || 0;
  const label = (side) => (comparison ? (side === 'target' ? 'Target' : 'Current') : 'Component');

  if (!(total > 0)) errors.push('Enter a total investment amount greater than zero.');
  if (components.length === 0) errors.push('Add at least one allocation component.');

  const retainedList = (retainedItems || []).filter((r) => sides.some((s) => retainedOf(r, s) > 0));
  const retainedTotal = {};
  sides.forEach((side) => {
    const t = retainedList.reduce((s, r) => s + retainedOf(r, side), 0);
    retainedTotal[side] = t;
    if ((retainedItems || []).some((r) => retainedOf(r, side) < 0) || t > total) {
      errors.push(comparison
        ? `${label(side)} retained cash and term deposits must total between $0 and the total investment amount.`
        : 'Retained cash and term deposits must total between $0 and the total investment amount.');
    }
    const shareTotal = components.reduce((s, c) => s + weightOf(c, side), 0);
    if (components.length > 0 && Math.abs(shareTotal - 100) > 0.01) {
      errors.push(`${label(side)} weights must total 100% (currently ${shareTotal.toFixed(2)}%).`);
    }
  });
  if (errors.length) return empty(warnings, errors);

  // Merge every component's rows into fund lines, keyed so the same fund held
  // via two portfolios (or on both sides) collapses into one line, but a direct
  // asset-class line never merges with a fund.
  const map = new Map();
  const addRow = (side, key, name, code, acg, frac, isDirect = false, isCustom = false) => {
    let row = map.get(key);
    if (!row) {
      row = {
        key, name, code, sector: sectorFor(acg), acg: (acg || '').trim(),
        frac: 0, curFrac: 0, isDirect, isCustom: false,
      };
      map.set(key, row);
    }
    if (side === 'target') row.frac += frac; else row.curFrac += frac;
    if (name) row.name = name;
    if (code && !row.code) row.code = code;
    if (isCustom) row.isCustom = true;
  };

  const reported = new Set(); // components already warned/errored — once, not per side

  // Spread one holdings set (weights normalised by their own sum) across a
  // component's share of the invested amount — shared by registry portfolios
  // and Lab portfolios. `refKey` dedupes the messages across sides.
  const allocateHoldings = (side, refKey, name, holdings, shareFrac, investableFrac) => {
    const sum = (holdings || []).reduce((s, h) => s + (h.weight || 0), 0);
    if (!reported.has(refKey)) {
      reported.add(refKey);
      if (!holdings || holdings.length === 0) {
        errors.push(`No target holdings found for ${name} — it cannot be allocated.`);
      } else if (sum <= 0) {
        errors.push(`Target weights for ${name} sum to zero — it cannot be allocated.`);
      } else if (Math.abs(sum - 100) > 0.5) {
        warnings.push(
          `${name}: target weights foot to ${sum.toFixed(2)}% — normalised to 100% for this allocation.`
        );
      }
    }
    if (!holdings || holdings.length === 0 || sum <= 0) return;
    holdings.forEach((h) => {
      const frac = ((h.weight || 0) / sum) * shareFrac * investableFrac;
      if (frac === 0) return;
      const key = h.apir_code || h.tr_code || h.asset_name;
      if (!key) return;
      addRow(side, key, h.asset_name || key, h.apir_code || h.tr_code || '',
        h.asset_class_group, frac);
    });
  };

  sides.forEach((side) => {
    const investableFrac = (total - retainedTotal[side]) / total;
    components.forEach((c) => {
      const shareFrac = weightOf(c, side) / 100;
      if (shareFrac <= 0) return;

      if (c.type === 'portfolio') {
        const code = c.ref;
        allocateHoldings(side, code, nameByCode[code] || code, holdingsByCode[code],
          shareFrac, investableFrac);
      } else if (c.type === 'lab') {
        const lab = labById[c.ref];
        const refKey = `lab:${c.ref}`;
        const name = lab?.name || `Portfolio Lab portfolio ${c.ref}`;
        const unmatched = lab?.unmatched || [];
        if (lab && (lab.geared || unmatched.length)) {
          if (!reported.has(refKey)) {
            reported.add(refKey);
            if (lab.geared) {
              errors.push(`${name} is geared (it has negative weights) — geared portfolios can't be allocated.`);
            }
            if (unmatched.length) {
              errors.push(`${name} cannot be allocated — ${unmatched
                .map((u) => `${u.fund_name} (${u.fund_code}): ${u.reason}`).join('; ')}.`);
            }
          }
          return;
        }
        allocateHoldings(side, refKey, name, lab?.holdings, shareFrac, investableFrac);
      } else if (c.type === 'fund' || c.type === 'manual') {
        const f = c.ref || {};
        const key = f.apir_code || f.tr_code || f.fund_name;
        if (!key) return;
        addRow(side, key, f.fund_name || key, f.apir_code || f.tr_code || '',
          f.asset_class_group, shareFrac * investableFrac, false, !!f.is_custom);
      } else if (c.type === 'direct') {
        const ac = c.ref;
        if (!ac) return;
        addRow(side, `direct:${ac}`, `${ac} — direct allocation`, '', ac,
          shareFrac * investableFrac, true);
      }
    });
  });

  if (errors.length) return empty(warnings, errors);

  const rows = [...map.values()];
  // Each retained item is its own pinned row; all behave identically (off the
  // top, defensive) — the split is presentation only.
  const retainedRows = retainedList.map((r, i) => ({
    key: `__retained${i}__`, name: r.name, code: '', sector: null,
    frac: retainedOf(r, 'target') / total,
    curFrac: retainedOf(r, 'current') / total,
  }));
  const allRows = [...retainedRows, ...rows];

  // One rounding pass per side over every row (retained included): percentages
  // in integer hundredths of a percent footing to exactly 100.00%, dollars in
  // whole dollars footing to exactly the total. Dollars round from the exact
  // fractions, independently of the rounded percentages. Change figures are the
  // difference of the ROUNDED sides so they foot to zero / the total.
  const roundSide = (fracField, pctField, dollarField) => {
    const pctUnits = largestRemainderRound(allRows.map((r) => r[fracField] * 10000), 10000);
    const dollars = largestRemainderRound(allRows.map((r) => r[fracField] * total), Math.round(total));
    allRows.forEach((r, i) => {
      r[pctField] = pctUnits[i] / 100;
      r[dollarField] = dollars[i];
    });
  };
  roundSide('frac', 'pct', 'dollars');
  if (comparison) {
    roundSide('curFrac', 'curPct', 'curDollars');
    allRows.forEach((r) => {
      r.changePct = round2(r.pct - r.curPct);
      r.changeDollars = r.dollars - r.curDollars;
    });
  }

  const fundRows = allRows.slice(retainedRows.length);

  // Group into display sectors; rows within a sector keep descending target
  // dollar order (rows being sold out — target 0 — sink to the bottom).
  const bySector = new Map();
  fundRows.forEach((r) => {
    if (!bySector.has(r.sector)) bySector.set(r.sector, []);
    bySector.get(r.sector).push(r);
  });
  const sectors = [...bySector.entries()]
    .map(([sectorLabel, sectorRows]) => {
      const s = {
        label: sectorLabel,
        rows: sectorRows.sort((a, b) => b.dollars - a.dollars
          || (b.curDollars || 0) - (a.curDollars || 0)
          || a.name.localeCompare(b.name)),
        subtotalPct: sumPct(sectorRows, 'pct'),
        subtotalDollars: sumOf(sectorRows, 'dollars'),
      };
      if (comparison) {
        s.subtotalCurPct = sumPct(sectorRows, 'curPct');
        s.subtotalCurDollars = sumOf(sectorRows, 'curDollars');
        s.changePct = round2(s.subtotalPct - s.subtotalCurPct);
        s.changeDollars = s.subtotalDollars - s.subtotalCurDollars;
      }
      return s;
    })
    .sort((a, b) => {
      const ka = sectorSortKey(a.label, classOrder);
      const kb = sectorSortKey(b.label, classOrder);
      return ka[0] - kb[0] || ka[1] - kb[1] || ka[2].localeCompare(kb[2]);
    });

  const totalRow = { pct: sumPct(allRows, 'pct'), dollars: sumOf(allRows, 'dollars') };
  if (comparison) {
    totalRow.curPct = sumPct(allRows, 'curPct');
    totalRow.curDollars = sumOf(allRows, 'curDollars');
    totalRow.changePct = round2(totalRow.pct - totalRow.curPct);
    totalRow.changeDollars = totalRow.dollars - totalRow.curDollars;
  }

  // Growth/defensive split over the rounded figures: a row is growth when its
  // broad asset class is in the taxonomy's growth list; everything else —
  // retained cash included — is defensive, so the two halves foot to the total.
  let exposure = null;
  if (growthClasses && growthClasses.length) {
    const growthSet = new Set(growthClasses);
    const growthRows = fundRows.filter((r) => growthSet.has(r.acg));
    const split = (pctField, dollarField, totPct, totDollars) => {
      const growthPct = sumPct(growthRows, pctField);
      const growthDollars = sumOf(growthRows, dollarField);
      return {
        growthPct,
        growthDollars,
        defensivePct: round2(totPct - growthPct),
        defensiveDollars: totDollars - growthDollars,
      };
    };
    exposure = split('pct', 'dollars', totalRow.pct, totalRow.dollars);
    if (comparison) {
      exposure.current = split('curPct', 'curDollars', totalRow.curPct, totalRow.curDollars);
    }
  }

  return { retainedRows, sectors, totalRow, exposure, comparison, warnings, errors };
}
